import { useEffect, useState, useCallback, useRef } from 'react';
import type { FormEvent } from 'react';
import { useAuth } from '../context/AuthContext';
import { apiGet, apiPost, apiFetch } from '../lib/api';
import { Feedback } from '../components/Feedback';
import { CopyButton } from '../components/CopyButton';

interface KeyScope {
  schema_code: string;
  tier_access: number;
}

interface KeyRow {
  id: number;
  label: string | null;
  kind: 'service' | 'personal';
  keyPrefix: string | null;
  createdByUserId: number | null;
  createdAt: number;
  lastUsedAt: number | null;
  lastUsedIp: string | null;
  revokedAt: number | null;
  expiresAt: number | null;
  rotatedFromId: number | null;
  isLegacyWildcard: boolean;
  rateLimitPerHour: number | null;
  status: 'active' | 'expiring' | 'revoked' | 'expired';
  scopes: KeyScope[];
}

interface ListResponse {
  keys: KeyRow[];
}

interface MintResponse {
  apiKey: string;
  message: string;
  key: KeyRow;
}

interface RotateResponse {
  apiKey: string;
  message: string;
  graceDays: number;
  predecessorId: number;
  key: KeyRow;
}

const TIER_NAMES = ['NANO', 'MICRO', 'FULL'] as const;

function fmtTime(t: number | null): string {
  if (!t) return 'never';
  // last_used_at is stored in ms, others in seconds. Heuristic: if > 1e12, ms.
  const ms = t > 1e12 ? t : t * 1000;
  return new Date(ms).toLocaleString();
}

function statusBadge(s: KeyRow['status']): { label: string; color: string } {
  switch (s) {
    case 'active': return { label: 'active', color: 'var(--color-accent)' };
    case 'expiring': return { label: 'expiring', color: 'var(--color-warn)' };
    case 'revoked': return { label: 'revoked', color: 'var(--color-text-muted)' };
    case 'expired': return { label: 'expired', color: 'var(--color-text-muted)' };
  }
}

/**
 * Route: `/dashboard/keys`. List, mint, rotate, and revoke org API keys.
 *
 * Plaintext keys are visible only at the moment they are created (mint or
 * rotate response). The list itself shows metadata only — label, prefix,
 * scopes, last used, status. To get a key value on a fresh device, mint a
 * new one or rotate an existing one.
 */
export function KeysPage() {
  const { user, isAdmin, canUseSchema } = useAuth();
  const [keys, setKeys] = useState<KeyRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error' | 'warn'; msg: string } | null>(null);
  const [showMint, setShowMint] = useState(false);
  const [revealed, setRevealed] = useState<{ id: number; plaintext: string; message: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const operationPending = useRef(false);

  const beginOperation = useCallback(() => {
    if (operationPending.current) return false;
    operationPending.current = true;
    setBusy(true);
    return true;
  }, []);

  const endOperation = useCallback(() => {
    operationPending.current = false;
    setBusy(false);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiGet<ListResponse>('/v1/keys');
      setKeys(res.keys);
    } catch (err) {
      setFeedback({ type: 'error', msg: err instanceof Error ? err.message : 'failed to load keys' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function rotate(id: number) {
    if (!beginOperation()) return;
    setFeedback(null);
    try {
      const res = await apiPost<RotateResponse>(`/v1/keys/${id}/rotate`, {});
      setRevealed({ id: res.key.id, plaintext: res.apiKey, message: res.message });
      await load();
    } catch (err) {
      setFeedback({ type: 'error', msg: err instanceof Error ? err.message : 'rotate failed' });
    } finally {
      endOperation();
    }
  }

  async function revoke(id: number, label: string | null) {
    if (operationPending.current) return;
    if (!confirm(`Revoke key "${label ?? id}"? This is immediate and not reversible.`)) return;
    if (!beginOperation()) return;
    setFeedback(null);
    try {
      await apiFetch(`/v1/keys/${id}`, { method: 'DELETE' });
      await load();
      setFeedback({ type: 'success', msg: 'Key revoked.' });
    } catch (err) {
      setFeedback({ type: 'error', msg: err instanceof Error ? err.message : 'revoke failed' });
    } finally {
      endOperation();
    }
  }

  return (
    <div>
      <div className="text-muted" style={{ marginBottom: 8 }}>── api keys ─────────────────────────────────────</div>
      <p className="text-muted" style={{ marginBottom: 16, fontSize: 'var(--font-size-sm)' }}>
        keys belong to <strong>{user?.orgName ?? '—'}</strong>. plaintext is shown only when a key is minted or rotated.
        if you need a key on a new device, rotate the existing one (the predecessor stays valid for 7 days) or mint a fresh one.
      </p>

      {feedback && <Feedback type={feedback.type} message={feedback.msg} />}

      {revealed && (
        <div className="box" style={{ marginBottom: 16, borderColor: 'var(--color-accent)' }}>
          <p className="label">new api key — copy now, this is the only time you will see it</p>
          <pre className="hex-display" style={{ overflowWrap: 'break-word', whiteSpace: 'pre-wrap' }}>{revealed.plaintext}</pre>
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <CopyButton text={revealed.plaintext} label="copy api key" />
            <button type="button" className="btn" onClick={() => setRevealed(null)}>i&apos;ve saved it</button>
          </div>
          <p className="text-muted" style={{ fontSize: 'var(--font-size-sm)', marginTop: 8 }}>{revealed.message}</p>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <span className="text-muted" style={{ fontSize: 'var(--font-size-sm)' }}>{keys.length} key{keys.length === 1 ? '' : 's'}</span>
        <button type="button" className="btn btn-primary" disabled={busy} onClick={() => setShowMint((v) => !v)}>
          {showMint ? 'cancel' : '+ mint new key'}
        </button>
      </div>

      {showMint && (
        <MintForm
          onCancel={() => setShowMint(false)}
          onMinted={(plaintext, message, key) => {
            setShowMint(false);
            setRevealed({ id: key.id, plaintext, message });
            load();
          }}
          canUseSchema={canUseSchema}
          disabled={busy}
          beginOperation={beginOperation}
          endOperation={endOperation}
        />
      )}

      {loading ? (
        <p className="text-muted">loading…</p>
      ) : keys.length === 0 ? (
        <div className="box">
          <p>no api keys yet. mint one above to start authenticating against the api.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {keys.map((k) => {
            const badge = statusBadge(k.status);
            const canRotate = isAdmin || (!k.isLegacyWildcard && k.scopes.every((scope) => canUseSchema(scope.schema_code)));
            return (
              <div key={k.id} className="box">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 240 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <strong>{k.label ?? `key #${k.id}`}</strong>
                      <span style={{ fontSize: 'var(--font-size-sm)', color: badge.color, padding: '1px 6px', border: `1px solid ${badge.color}`, borderRadius: 4 }}>
                        {badge.label}
                      </span>
                      {k.isLegacyWildcard && (
                        <span className="text-muted" style={{ fontSize: 'var(--font-size-sm)' }} title="created before per-schema scoping">legacy</span>
                      )}
                      <span className="text-muted" style={{ fontSize: 'var(--font-size-sm)' }}>{k.kind}</span>
                    </div>
                    <div style={{ fontSize: 'var(--font-size-sm)', marginTop: 4 }}>
                      <span className="text-muted">prefix:</span> <code>{k.keyPrefix ?? '—'}…</code>
                    </div>
                    <div style={{ fontSize: 'var(--font-size-sm)', marginTop: 4 }}>
                      <span className="text-muted">scopes:</span>{' '}
                      {k.isLegacyWildcard ? (
                        <em className="text-muted">all schemas (legacy)</em>
                      ) : k.scopes.length === 0 ? (
                        <em className="text-muted">none</em>
                      ) : (
                        k.scopes.map((s) => (
                          <span key={s.schema_code} style={{ marginRight: 8 }}>
                            <code>{s.schema_code}</code>
                            <span className="text-muted"> · {TIER_NAMES[s.tier_access] ?? `tier ${s.tier_access}`}</span>
                          </span>
                        ))
                      )}
                    </div>
                    <div style={{ fontSize: 'var(--font-size-sm)', marginTop: 4 }}>
                      <span className="text-muted">last used:</span> {fmtTime(k.lastUsedAt)}
                      {k.lastUsedIp && <span className="text-muted"> from {k.lastUsedIp}</span>}
                    </div>
                    <div style={{ fontSize: 'var(--font-size-sm)' }}>
                      <span className="text-muted">created:</span> {fmtTime(k.createdAt)}
                      {k.expiresAt && <span className="text-muted"> · expires {fmtTime(k.expiresAt)}</span>}
                    </div>
                    {k.rotatedFromId && (
                      <div style={{ fontSize: 'var(--font-size-sm)' }} className="text-muted">
                        successor of key #{k.rotatedFromId}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {k.status === 'active' || k.status === 'expiring' ? (
                      <>
                        <button type="button" className="btn" disabled={busy || !canRotate} title={!canRotate ? 'An owner or admin must rotate keys outside your schema access.' : undefined} onClick={() => rotate(k.id)}>rotate</button>
                        {isAdmin && (
                          <button type="button" className="btn" disabled={busy} onClick={() => revoke(k.id, k.label)}>revoke</button>
                        )}
                      </>
                    ) : null}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

interface MintFormProps {
  onCancel: () => void;
  onMinted: (plaintext: string, message: string, key: KeyRow) => void;
  canUseSchema: (s: string) => boolean;
  disabled: boolean;
  beginOperation: () => boolean;
  endOperation: () => void;
}

interface SchemaListItem { id: number; code: string; name?: string; implemented: boolean }

function MintForm({ onCancel, onMinted, canUseSchema, disabled, beginOperation, endOperation }: MintFormProps) {
  const [label, setLabel] = useState('');
  const [kind, setKind] = useState<'service' | 'personal'>('service');
  const [schemas, setSchemas] = useState<SchemaListItem[]>([]);
  const [selected, setSelected] = useState<Map<string, number>>(new Map()); // schema_code → tier
  const [certifiedTiers, setCertifiedTiers] = useState<Map<string, number>>(new Map());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let disposed = false;
    Promise.all([
      apiGet<{ schemas: SchemaListItem[] }>('/v1/schemas'),
      apiGet<Array<{ schema: string; tier: number; status: string; expiresAt: number | null }>>('/v1/certs'),
    ])
      .then(([res, certs]) => {
        if (disposed) return;
        const tiers = new Map<string, number>();
        const now = Math.floor(Date.now() / 1000);
        for (const cert of certs) {
          if (cert.status === 'active' && (cert.expiresAt === null || cert.expiresAt > now)) {
            tiers.set(cert.schema, Math.max(tiers.get(cert.schema) ?? 0, cert.tier));
          }
        }
        setCertifiedTiers(tiers);
        setSchemas((res.schemas ?? []).filter((schema) => schema.implemented));
      })
      .catch((err: Error) => setError(err.message || 'Unable to load available scopes.'));
    return () => { disposed = true; };
  }, []);

  function toggleSchema(code: string) {
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(code)) next.delete(code);
      else next.set(code, 0);
      return next;
    });
  }

  function setTier(code: string, tier: number) {
    setSelected((prev) => {
      const next = new Map(prev);
      next.set(code, tier);
      return next;
    });
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!label.trim()) {
      setError('label is required');
      return;
    }
    if (selected.size === 0) {
      setError('pick at least one schema scope');
      return;
    }
    if (!beginOperation()) return;
    setBusy(true);
    try {
      const body = {
        label: label.trim(),
        kind,
        scopes: Array.from(selected.entries()).map(([schema, tier]) => ({ schema, tier })),
      };
      const res = await apiPost<MintResponse>('/v1/keys', body);
      onMinted(res.apiKey, res.message, res.key);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'mint failed');
    } finally {
      setBusy(false);
      endOperation();
    }
  }

  return (
    <form onSubmit={submit} className="box" style={{ marginBottom: 16 }}>
      <p className="label">mint new api key</p>
      {error && <Feedback type="error" message={error} />}

      <div className="form-row">
        <label className="label" htmlFor="key-label">label</label>
        <input
          type="text"
          id="key-label"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="e.g. EMRG production fleet"
          className="form-input"
          maxLength={80}
        />
      </div>

      <div className="form-row">
        <label className="label">kind</label>
        <div style={{ display: 'flex', gap: 12 }}>
          <label style={{ display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}>
            <input type="radio" name="kind" checked={kind === 'service'} onChange={() => setKind('service')} />
            <span>service / device fleet</span>
          </label>
          <label style={{ display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}>
            <input type="radio" name="kind" checked={kind === 'personal'} onChange={() => setKind('personal')} />
            <span>personal / cli</span>
          </label>
        </div>
      </div>

      <div className="form-row">
        <label className="label">schema scopes</label>
        <p className="text-muted" style={{ fontSize: 'var(--font-size-sm)', marginBottom: 6 }}>
          pick which standards this key can access. tier is the maximum payload size the key may use within that standard.
        </p>
        {schemas.length === 0 ? (
          <p className="text-muted">no schemas available</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {schemas.map((s) => {
              const inAllowlist = canUseSchema(s.code);
              const maxTier = certifiedTiers.get(s.code);
              const allowed = inAllowlist && maxTier !== undefined;
              const sel = selected.get(s.code);
              return (
                <div key={s.code} style={{ display: 'flex', alignItems: 'center', gap: 8, opacity: allowed ? 1 : 0.5 }}>
                  <label style={{ display: 'flex', gap: 6, alignItems: 'center', cursor: allowed ? 'pointer' : 'not-allowed', minWidth: 100 }}>
                    <input
                      type="checkbox"
                      checked={selected.has(s.code)}
                      disabled={disabled || !allowed}
                      onChange={() => toggleSchema(s.code)}
                    />
                    <code>{s.code}</code>
                  </label>
                  {selected.has(s.code) && (
                    <select
                      value={sel ?? 0}
                      disabled={disabled}
                      onChange={(e) => setTier(s.code, Number(e.target.value))}
                      className="form-input"
                      style={{ width: 'auto' }}
                    >
                      <option value={0}>NANO</option>
                      <option value={1} disabled={(maxTier ?? -1) < 1}>MICRO</option>
                      <option value={2} disabled={(maxTier ?? -1) < 2}>FULL</option>
                    </select>
                  )}
                  {!allowed && <span className="text-muted" style={{ fontSize: 'var(--font-size-sm)' }}>{inAllowlist ? 'active certification required' : 'not in your allowlist'}</span>}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        <button type="submit" className="btn btn-primary" disabled={disabled || selected.size === 0}>
          {busy ? 'minting…' : 'mint key'}
        </button>
        <button type="button" className="btn" disabled={disabled} onClick={onCancel}>cancel</button>
      </div>
    </form>
  );
}
